import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sparkle, Upload, Plus, MagnifyingGlass, Eye, PencilSimple, Trash, FolderOpen, Warning, CircleNotch as Spinner, ArrowCounterClockwise } from "@phosphor-icons/react";
import AIButton from "@/components/AIButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MACRO_AREAS = [
  "Vendite", "Marketing", "IT", "HR", "Amministrazione", "Finanza",
  "Produzione", "Ufficio Tecnico", "Magazzino", "Acquisti", "Logistica",
  "Customer Service", "Operazioni", "Altro",
];

const EMPTY_JD = {
  title: "", macro_area: "",
  responsibilities: [], technical_skills: [], soft_skills: [], kpi: [], notes: "",
};

const TABS = [
  { key: "tutti", label: "Tutti" },
  { key: "standard", label: "Standard" },
  { key: "custom", label: "Personalizzati" },
  { key: "cestino", label: "Cestino" },
];

function toLines(arr) { return (arr || []).join("\n"); }
function fromLines(text) { return (text || "").split("\n").map(s => s.trim()).filter(Boolean); }

function badgeFor(jd) {
  if (jd.is_standard) return <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-500/20 text-[11px]">Standard</Badge>;
  if (jd.is_ai_generated) return <Badge className="bg-[#1B4FD8]/10 text-[#1B4FD8] border-[#1B4FD8]/20 text-[11px]">Generato</Badge>;
  return <Badge className="bg-orange-500/10 text-orange-700 border-orange-500/20 text-[11px]">Custom</Badge>;
}

function daysRemainingInTrash(deletedAtIso) {
  if (!deletedAtIso) return 30;
  try {
    const d = new Date(deletedAtIso);
    const elapsedMs = Date.now() - d.getTime();
    const remainingDays = 30 - Math.floor(elapsedMs / 86400000);
    return Math.max(0, remainingDays);
  } catch { return 30; }
}

export default function MansionariLibrary() {
  const { activeWorkspaceId } = useAuth();
  const [activeJds, setActiveJds] = useState([]);
  const [trashedJds, setTrashedJds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterArea, setFilterArea] = useState("__all__");
  const [activeTab, setActiveTab] = useState("tutti");

  // Dialog state
  const [viewJd, setViewJd] = useState(null);
  const [editJd, setEditJd] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { jd, linked_positions, confirmed }
  const [showGenerate, setShowGenerate] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [busy, setBusy] = useState(false);

  const STANDARD_TARGET = 49;

  const reloadJds = useCallback(async () => {
    if (!activeWorkspaceId) { setActiveJds([]); setTrashedJds([]); return; }
    const [actRes, trashRes] = await Promise.all([
      axios.get(`${API}/library/job-descriptions?workspace_id=${activeWorkspaceId}&status=attivi`, { withCredentials: true }),
      axios.get(`${API}/library/job-descriptions?workspace_id=${activeWorkspaceId}&status=cestino`, { withCredentials: true }),
    ]);
    setActiveJds(actRes.data || []);
    setTrashedJds(trashRes.data || []);
  }, [activeWorkspaceId]);

  useEffect(() => {
    if (!activeWorkspaceId) { setLoading(false); return; }
    setLoading(true);
    reloadJds().catch(() => toast.error("Errore caricamento mansionari")).finally(() => setLoading(false));
  }, [activeWorkspaceId, reloadJds]);

  const workspaceId = activeWorkspaceId;

  const standardCount = useMemo(() => activeJds.filter(j => j.is_standard).length, [activeJds]);

  const areasAvailable = useMemo(() => {
    const source = activeTab === "cestino" ? trashedJds : activeJds;
    return [...new Set(source.map(j => j.macro_area).filter(Boolean))].sort();
  }, [activeJds, trashedJds, activeTab]);

  const filtered = useMemo(() => {
    let list = activeTab === "cestino" ? trashedJds : activeJds;
    if (activeTab === "standard") list = list.filter(j => j.is_standard);
    if (activeTab === "custom") list = list.filter(j => !j.is_standard);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(j => (j.title || "").toLowerCase().includes(q));
    if (filterArea !== "__all__") list = list.filter(j => j.macro_area === filterArea);
    return list;
  }, [activeJds, trashedJds, activeTab, search, filterArea]);

  // ── Seed (3-state) ──
  const [seedProgress, setSeedProgress] = useState(null); // null | {current, target}
  const seedPollingRef = useRef(null);

  const seedStandard = async () => {
    if (!workspaceId || seedProgress) return;
    setSeedProgress({ current: standardCount, target: STANDARD_TARGET });
    try {
      const { data } = await axios.post(`${API}/library/job-descriptions/seed-standard`,
        { workspace_id: workspaceId }, { withCredentials: true });
      const target = data.target || STANDARD_TARGET;
      const existing = data.existing ?? standardCount;
      if (!data.started || existing >= target) {
        setSeedProgress(null);
        toast.info(data.message || "Libreria standard gia completa");
        await reloadJds();
        return;
      }
      setSeedProgress({ current: existing, target });
      // BUG-42/44: polling lightweight via /seed-standard/status (count diretto)
      seedPollingRef.current = setInterval(async () => {
        try {
          const { data: st } = await axios.get(
            `${API}/library/job-descriptions/seed-standard/status?workspace_id=${workspaceId}`,
            { withCredentials: true });
          const stdCount = st.progress || 0;
          setSeedProgress((p) => p ? { ...p, current: stdCount } : p);
          if (st.status === "done" || stdCount >= target) {
            clearInterval(seedPollingRef.current);
            seedPollingRef.current = null;
            setSeedProgress(null);
            await reloadJds();
            toast.success(`Libreria popolata: ${stdCount} mansionari pronti`);
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e) {
      setSeedProgress(null);
      toast.error(e.response?.data?.detail || "Errore");
    }
  };
  useEffect(() => () => { if (seedPollingRef.current) clearInterval(seedPollingRef.current); }, []);

  // 3-state seed button: hidden | "Genera" | "Implementa (N/M)"
  const seedButtonState = useMemo(() => {
    if (seedProgress) return "running";
    if (standardCount === 0) return "genera";
    if (standardCount < STANDARD_TARGET) return "implementa";
    return "hidden";
  }, [standardCount, seedProgress]);

  // ── Delete flow with linked-position warning ──
  const requestDelete = async (jd) => {
    // Pre-check linked positions
    let linked = 0;
    try {
      const { data } = await axios.get(`${API}/positions?status=aperte`, { withCredentials: true });
      linked = (data || []).filter(p => p.linked_job_description_id === jd.id).length;
    } catch { /* fallback: server response will tell */ }
    setDeleteTarget({ jd, linked_positions: linked, confirmed: false });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/library/job-descriptions/${deleteTarget.jd.id}`, { withCredentials: true });
      toast.success("Mansionario spostato nel cestino");
      setDeleteTarget(null);
      await reloadJds();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const restoreJd = async (jdId) => {
    try {
      await axios.post(`${API}/library/job-descriptions/${jdId}/restore`, {}, { withCredentials: true });
      toast.success("Mansionario ripristinato");
      await reloadJds();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const saveEdit = async () => {
    if (!editJd) return;
    setBusy(true);
    try {
      if (editJd._isNew) {
        const payload = {
          workspace_id: workspaceId,
          title: editJd.title, macro_area: editJd.macro_area,
          responsibilities: editJd.responsibilities,
          technical_skills: editJd.technical_skills,
          soft_skills: editJd.soft_skills,
          kpi: editJd.kpi, notes: editJd.notes || "",
        };
        await axios.post(`${API}/library/job-descriptions`, payload, { withCredentials: true });
        toast.success("Mansionario creato");
      } else {
        const { id } = editJd;
        const { _isNew, _id, created_at, is_standard, is_ai_generated, ...rest } = editJd;  // eslint-disable-line no-unused-vars
        await axios.put(`${API}/library/job-descriptions/${id}`, rest, { withCredentials: true });
        toast.success("Mansionario aggiornato");
      }
      setEditJd(null);
      await reloadJds();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="text-sm text-[#0D1F3C]/50 py-10 text-center" data-testid="mansionari-loading">Caricamento mansionari...</div>;
  }

  const totalAll = activeJds.length;
  const totalTrash = trashedJds.length;
  const showEmpty = totalAll === 0 && totalTrash === 0;

  if (showEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="mansionari-empty">
        <FolderOpen size={48} weight="duotone" className="text-[#0D1F3C]/30 mb-4" />
        <h3 className="text-lg font-semibold text-[#0D1F3C] mb-2">Nessun mansionario presente</h3>
        <p className="text-sm text-[#0D1F3C]/60 mb-6 max-w-md">Popola la libreria con i mansionari standard piu usati nelle PMI italiane, oppure creane uno manualmente.</p>
        <div className="flex gap-3">
          <AIButton
            onClick={seedStandard}
            label="Genera mansionari standard"
            loadingLabel={seedProgress ? `Generazione in corso... ${seedProgress.current}/${seedProgress.target}` : "Generazione in corso..."}
            successLabel={null}
            errorPrefix="Errore seed"
            variant="primary"
            disabled={!!seedProgress}
            loading={!!seedProgress}
            dataTestid="seed-standard-btn"
          />
          <Button onClick={() => setEditJd({ ...EMPTY_JD, _isNew: true })} variant="outline" className="border-[#0D1F3C]/15" data-testid="create-first-btn">Crea il primo manualmente</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="mansionari-library">
      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => setEditJd({ ...EMPTY_JD, _isNew: true })} className="bg-[#1B4FD8] hover:bg-[#0D1F3C] gap-2" data-testid="new-jd-btn">
          <Plus size={16} weight="bold" /> Nuovo Mansionario
        </Button>
        <Button onClick={() => setShowGenerate(true)} variant="outline" className="border-[#1B4FD8] text-[#1B4FD8] hover:bg-[#1B4FD8]/5 gap-2" data-testid="generate-ai-btn">
          <Sparkle size={16} weight="duotone" /> Genera automaticamente
        </Button>
        <Button onClick={() => setShowUpload(true)} variant="outline" className="border-[#0D1F3C]/15 gap-2" data-testid="upload-jd-btn">
          <Upload size={16} weight="duotone" /> Carica da File
        </Button>

        {seedButtonState !== "hidden" && (
          <AIButton
            onClick={seedStandard}
            label={seedButtonState === "genera"
              ? "Genera mansionari standard"
              : `Implementa mansionari standard (${standardCount}/${STANDARD_TARGET})`}
            loadingLabel={seedProgress ? `Generazione in corso... ${seedProgress.current}/${seedProgress.target}` : "Generazione in corso..."}
            successLabel={null}
            errorPrefix="Errore seed"
            variant="secondary"
            disabled={seedButtonState === "running"}
            loading={!!seedProgress}
            dataTestid="seed-standard-btn"
          />
        )}

        <div className="ml-auto text-xs text-[#0D1F3C]/60" data-testid="jd-count">
          {filtered.length} di {activeTab === "cestino" ? totalTrash : totalAll} mansionari
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#0D1F3C]/10" data-testid="mansionari-tabs">
        {TABS.map(t => {
          const count = t.key === "cestino"
            ? totalTrash
            : t.key === "standard" ? standardCount
            : t.key === "custom" ? activeJds.filter(j => !j.is_standard).length
            : totalAll;
          const isActive = activeTab === t.key;
          return (
            <button key={t.key}
              onClick={() => { setActiveTab(t.key); setSearch(""); setFilterArea("__all__"); }}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                isActive
                  ? (t.key === "cestino" ? "border-red-500 text-red-600" : "border-[#1B4FD8] text-[#1B4FD8]")
                  : "border-transparent text-[#0D1F3C]/60 hover:text-[#0D1F3C]"
              }`}
              data-testid={`tab-${t.key}`}>
              {t.label}
              <span className={`ml-2 text-[11px] px-1.5 py-0.5 rounded ${isActive ? "bg-[#1B4FD8]/10" : "bg-[#0D1F3C]/5"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/40" />
          <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca titolo..." className="pl-9" data-testid="jd-search" />
        </div>
        <Select value={filterArea} onValueChange={setFilterArea}>
          <SelectTrigger className="w-56" data-testid="jd-filter-area"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutte le aree</SelectItem>
            {areasAvailable.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map(jd => {
          const inTrash = activeTab === "cestino";
          const daysLeft = inTrash ? daysRemainingInTrash(jd.deleted_at) : null;
          return (
            <Card key={jd.id} className={`border-[#0D1F3C]/8 transition-all ${inTrash ? "opacity-80 hover:border-red-500/40" : "hover:border-[#1B4FD8]/40 hover:shadow-md"}`} data-testid={`jd-card-${jd.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-[#0D1F3C] leading-tight">{jd.title}</h3>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {inTrash && (
                      <Badge className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px]" data-testid={`days-remaining-${jd.id}`}>
                        {daysLeft} giorni residui
                      </Badge>
                    )}
                    {badgeFor(jd)}
                  </div>
                </div>
                <p className="text-xs text-[#0D1F3C]/60 mb-3">{jd.macro_area}</p>
                <ul className="space-y-1 mb-4 min-h-[40px]">
                  {(jd.responsibilities || []).slice(0, 2).map((r, i) => (
                    <li key={i} className="text-xs text-[#0D1F3C]/70 flex gap-2">
                      <span className="text-[#1B4FD8] mt-0.5">•</span>
                      <span className="line-clamp-1">{r}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  {inTrash ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setViewJd(jd)} className="flex-1 gap-1 text-xs border-[#0D1F3C]/15" data-testid={`view-${jd.id}`}>
                        <Eye size={14} weight="duotone" /> Vedi
                      </Button>
                      <Button size="sm" onClick={() => restoreJd(jd.id)} className="flex-1 gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`restore-${jd.id}`}>
                        <ArrowCounterClockwise size={14} weight="bold" /> Ripristina
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setViewJd(jd)} className="flex-1 gap-1 text-xs border-[#0D1F3C]/15" data-testid={`view-${jd.id}`}>
                        <Eye size={14} weight="duotone" /> Vedi
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditJd({ ...jd })} className="flex-1 gap-1 text-xs border-[#1B4FD8]/40 text-[#1B4FD8]" data-testid={`edit-${jd.id}`}>
                        <PencilSimple size={14} weight="duotone" /> Modifica
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => requestDelete(jd)} className="text-red-500 hover:text-red-600 hover:bg-red-50 px-2" data-testid={`delete-${jd.id}`}>
                        <Trash size={14} weight="duotone" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-10 text-sm text-[#0D1F3C]/50" data-testid="jd-no-results">
            {activeTab === "cestino"
              ? "Cestino vuoto. I mansionari eliminati restano qui per 30 giorni prima della rimozione definitiva."
              : "Nessun mansionario corrisponde ai filtri attuali."}
          </div>
        )}
      </div>

      {/* View modal */}
      <Dialog open={!!viewJd} onOpenChange={o => !o && setViewJd(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="view-jd-dialog">
          {viewJd && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#0D1F3C] flex items-center gap-3">
                  {viewJd.title} {badgeFor(viewJd)}
                </DialogTitle>
                <DialogDescription className="text-xs text-[#0D1F3C]/60">{viewJd.macro_area}</DialogDescription>
              </DialogHeader>
              <div className="space-y-5 py-2">
                {[
                  ["Responsabilità principali", viewJd.responsibilities],
                  ["Competenze tecniche", viewJd.technical_skills],
                  ["Soft skills", viewJd.soft_skills],
                  ["KPI di riferimento", viewJd.kpi],
                ].map(([label, list]) => (list && list.length > 0) && (
                  <div key={label}>
                    <h4 className="text-sm font-semibold text-[#0D1F3C] mb-2">{label}:</h4>
                    <ul className="space-y-1.5">
                      {list.map((item, i) => (
                        <li key={i} className="text-sm text-[#0D1F3C]/75 flex gap-2">
                          <span className="text-[#1B4FD8] mt-0.5">•</span><span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {viewJd.notes && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#0D1F3C] mb-2">Note:</h4>
                    <p className="text-sm text-[#0D1F3C]/75 whitespace-pre-line">{viewJd.notes}</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewJd(null)} className="border-[#0D1F3C]/15">Chiudi</Button>
                {!viewJd.deleted_at && (
                  <Button onClick={() => { setEditJd({ ...viewJd }); setViewJd(null); }} className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="view-to-edit-btn">Modifica</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit/Create modal */}
      <EditJdDialog
        open={!!editJd} jd={editJd} setJd={setEditJd}
        onSave={saveEdit} busy={busy}
      />

      {/* Delete confirm modal with linked-position warning */}
      <Dialog open={!!deleteTarget} onOpenChange={o => !o && !busy && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-jd-dialog">
          {deleteTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#0D1F3C] flex items-center gap-2">
                  <Trash size={20} weight="duotone" className="text-red-500" /> Eliminare mansionario?
                </DialogTitle>
                <DialogDescription className="text-sm text-[#0D1F3C]/70 pt-2">
                  Stai eliminando <strong>{deleteTarget.jd.title}</strong>. Sarà spostato nel Cestino e potrai ripristinarlo entro <strong>30 giorni</strong>; poi verrà eliminato definitivamente.
                </DialogDescription>
              </DialogHeader>

              {deleteTarget.linked_positions > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid="linked-warning">
                  <Warning size={20} weight="duotone" className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-900">
                    <strong>Attenzione:</strong> questo mansionario è collegato a <strong>{deleteTarget.linked_positions} posizion{deleteTarget.linked_positions === 1 ? "e attiva" : "i attive"}</strong>. Dopo l'eliminazione, le posizioni resteranno aperte ma il link sarà segnalato come "mansionario eliminato".
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 pt-2">
                <Checkbox id="confirm-irrev" checked={deleteTarget.confirmed}
                  onCheckedChange={(v) => setDeleteTarget(t => t ? { ...t, confirmed: !!v } : t)}
                  data-testid="delete-confirm-checkbox" />
                <Label htmlFor="confirm-irrev" className="text-xs text-[#0D1F3C]/80 cursor-pointer">
                  Confermo di voler spostare questo mansionario nel cestino.
                </Label>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={busy} className="border-[#0D1F3C]/15" data-testid="delete-cancel">Annulla</Button>
                <Button onClick={confirmDelete} disabled={busy || !deleteTarget.confirmed}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50" data-testid="delete-confirm">
                  {busy ? "Eliminazione..." : "Sposta nel Cestino"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Generate AI modal */}
      <GenerateAIDialog
        open={showGenerate} setOpen={setShowGenerate}
        workspaceId={workspaceId}
        onGenerated={async (jd) => {
          await reloadJds();
          setShowGenerate(false);
          setEditJd({ ...jd });
        }}
      />

      {/* Upload file modal */}
      <UploadFileDialog
        open={showUpload} setOpen={setShowUpload}
        workspaceId={workspaceId}
        onUploaded={async (jd) => {
          await reloadJds();
          setShowUpload(false);
          setEditJd({ ...jd });
        }}
      />
    </div>
  );
}

// ── Sub-components ──

function EditJdDialog({ open, jd, setJd, onSave, busy }) {
  if (!jd) return null;
  const setField = (k, v) => setJd({ ...jd, [k]: v });
  const isStandard = jd.is_standard;
  const isNew = jd._isNew;
  return (
    <Dialog open={open} onOpenChange={o => !o && setJd(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-jd-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0D1F3C]">{isNew ? "Nuovo Mansionario" : "Modifica Mansionario"}</DialogTitle>
          <DialogDescription className="text-xs text-[#0D1F3C]/60">
            {isNew ? "Compila i campi per creare un nuovo mansionario personalizzato." : "Modifica i campi e salva le modifiche."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Titolo *</Label>
              <Input value={jd.title || ""} onChange={e => setField("title", e.target.value)} data-testid="edit-title" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Macro-area *</Label>
              <Select value={jd.macro_area || ""} onValueChange={v => setField("macro_area", v)}>
                <SelectTrigger data-testid="edit-macro"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{MACRO_AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Responsabilità (una per riga)</Label>
            <Textarea rows={5} value={toLines(jd.responsibilities)} onChange={e => setField("responsibilities", fromLines(e.target.value))} data-testid="edit-resp" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Competenze tecniche (una per riga)</Label>
            <Textarea rows={4} value={toLines(jd.technical_skills)} onChange={e => setField("technical_skills", fromLines(e.target.value))} data-testid="edit-tech" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Soft skills (una per riga)</Label>
            <Textarea rows={4} value={toLines(jd.soft_skills)} onChange={e => setField("soft_skills", fromLines(e.target.value))} data-testid="edit-soft" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">KPI (uno per riga)</Label>
            <Textarea rows={3} value={toLines(jd.kpi)} onChange={e => setField("kpi", fromLines(e.target.value))} data-testid="edit-kpi" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Note</Label>
            <Textarea rows={3} value={jd.notes || ""} onChange={e => setField("notes", e.target.value)} data-testid="edit-notes" />
          </div>
          {isStandard && !isNew && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <Warning size={18} weight="duotone" className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">Modificando un mansionario standard, diventerà personalizzato (etichetta "Custom").</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setJd(null)} className="border-[#0D1F3C]/15" data-testid="edit-cancel">Annulla</Button>
          <Button onClick={onSave} disabled={busy || !jd.title || !jd.macro_area} className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="edit-save">
            {busy ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateAIDialog({ open, setOpen, workspaceId, onGenerated }) {
  const [title, setTitle] = useState("");
  const [macro, setMacro] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setTitle(""); setMacro(""); setNote(""); } }, [open]);

  const handleGenerate = async () => {
    if (!title.trim() || !macro) return toast.error("Titolo e macro-area obbligatori");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/library/job-descriptions/generate`, {
        workspace_id: workspaceId, title: title.trim(), macro_area: macro, note_hr: note.trim(),
      }, { withCredentials: true });
      toast.success("Mansionario creato");
      setOpen(false);
      onGenerated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogContent className="max-w-lg" data-testid="generate-ai-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0D1F3C] flex items-center gap-2"><Sparkle size={20} weight="duotone" className="text-[#1B4FD8]" /> Genera Mansionario</DialogTitle>
          <DialogDescription className="text-xs text-[#0D1F3C]/60">Selly strutturerà un mansionario professionale sul ruolo indicato.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs mb-1 block">Titolo ruolo *</Label>
            <Input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Es: Data Engineer" disabled={busy} data-testid="gen-title" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Macro-area *</Label>
            <Select value={macro} onValueChange={setMacro} disabled={busy}>
              <SelectTrigger data-testid="gen-macro"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{MACRO_AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Descrizione mansione & KPI</Label>
            <Textarea rows={4} value={note} onChange={e=>setNote(e.target.value)} disabled={busy}
              placeholder="Descrivi la mansione, le responsabilita chiave, i KPI attesi e qualsiasi info utile per la generazione del mansionario"
              data-testid="gen-note" />
          </div>
          <p className="text-xs text-[#0D1F3C]/50">La generazione richiede ~5 secondi.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} className="border-[#0D1F3C]/15" data-testid="gen-cancel">Annulla</Button>
          <Button onClick={handleGenerate} disabled={busy || !title.trim() || !macro} className="bg-[#1B4FD8] hover:bg-[#0D1F3C] disabled:opacity-70" data-testid="gen-submit">
            {busy
              ? <><Spinner size={14} className="mr-2 animate-spin" />Generazione in corso...</>
              : "Genera"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadFileDialog({ open, setOpen, workspaceId, onUploaded }) {
  const [title, setTitle] = useState("");
  const [macro, setMacro] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setTitle(""); setMacro(""); setFile(null); } }, [open]);

  const handleUpload = async () => {
    if (!title.trim() || !macro || !file) return toast.error("Titolo, area e file obbligatori");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("workspace_id", workspaceId);
      form.append("title", title.trim());
      form.append("macro_area", macro);
      form.append("file", file);
      const { data } = await axios.post(`${API}/library/job-descriptions/upload`, form, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File elaborato. Revisiona e salva.");
      onUploaded(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="upload-jd-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0D1F3C] flex items-center gap-2"><Upload size={20} weight="duotone" className="text-[#1B4FD8]" /> Carica Mansionario da File</DialogTitle>
          <DialogDescription className="text-xs text-[#0D1F3C]/60">Selly estrarrà responsabilità, skills e KPI dal documento.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs mb-1 block">Titolo ruolo *</Label>
            <Input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Es: Responsabile Logistica" data-testid="up-title" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Macro-area *</Label>
            <Select value={macro} onValueChange={setMacro}>
              <SelectTrigger data-testid="up-macro"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{MACRO_AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs mb-1 block">File mansionario (PDF o DOCX) *</Label>
            <Input type="file" accept=".pdf,.docx,.doc" onChange={e=>setFile(e.target.files?.[0] || null)} data-testid="up-file" />
            {file && <p className="text-xs text-[#0D1F3C]/60 mt-1">{file.name}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-[#0D1F3C]/15" data-testid="up-cancel">Annulla</Button>
          <AIButton
            onClick={handleUpload}
            label="Carica e Processa"
            loadingLabel="Elaborazione in corso..."
            successLabel={null}
            errorPrefix="Errore upload"
            variant="primary"
            disabled={!title.trim() || !macro || !file}
            dataTestid="up-submit"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
